import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import api from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import { ArrowLeft, Loader2, Megaphone, ArrowRight, Download, Flame, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const RADII = [0, 250, 500, 1000];
const WINDOWS = [7, 15, 30, 60, 90];
const RADIUS_LABEL = { 0: "Exact", 250: "250 m", 500: "500 m", 1000: "1 km" };

function fmt(n) { return (n ?? 0).toLocaleString("en-US"); }

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 12);
    } else {
      const bounds = points.map((p) => [p.latitude, p.longitude]);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [map, points]);
  return null;
}

function HeatLayer({ points, radius, window, maxCount }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return undefined;
    const heatData = points.map((p) => {
      const c = p.cells.find((x) => x.radius_m === radius && x.time_window_days === window)?.count ?? 0;
      // normalise to [0,1] for leaflet.heat intensity
      return [p.latitude, p.longitude, Math.max(0.05, c / Math.max(1, maxCount))];
    });
    const layer = L.heatLayer(heatData, {
      radius: 45,
      blur: 30,
      maxZoom: 12,
      max: 1.0,
      gradient: {
        0.0: "#0A0A0A",
        0.35: "#0052FF",
        0.6: "#FF3E00",
        0.85: "#FF8A3D",
        1.0: "#FFE000",
      },
    }).addTo(map);
    return () => { map.removeLayer(layer); };
  }, [map, points, radius, window, maxCount]);
  return null;
}

export default function BatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(false);
  const [mapRadius, setMapRadius] = useState(1000);
  const [mapWindow, setMapWindow] = useState(30);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    (async () => {
      try {
        const skip = (page - 1) * pageSize;
        const { data } = await api.get(`/audience/batches/${id}`, {
          params: { skip, limit: pageSize, map_sample: 1500 },
        });
        setData(data);
      } catch {
        toast.error("Audience batch not found");
        navigate("/search");
      } finally {
        setLoading(false);
        setPaging(false);
      }
    })();
  }, [id, navigate, page, pageSize]);

  const matrix = useMemo(() => {
    if (!data) return null;
    const m = {};
    for (const r of RADII) m[r] = {};
    for (const c of data.aggregated) {
      if (!m[c.radius_m]) m[c.radius_m] = {};
      m[c.radius_m][c.time_window_days] = c.count;
    }
    return m;
  }, [data]);

  // Map points come pre-sampled from the backend (across the full batch),
  // so the map always reflects every coordinate regardless of table pagination.
  const mapPoints = useMemo(() => data?.map_points || data?.points || [], [data]);

  if (loading) {
    return (
      <div className="px-8 md:px-12 py-10">
        <p className="text-sm text-[#555] flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading audience…
        </p>
      </div>
    );
  }
  if (!data) return null;

  const { batch, points } = data;
  const totalPoints = data.total_points ?? points.length;
  const isSingle = (batch.kind || "bulk") === "single";
  const title = batch.label || batch.filename || `Audience ${batch.id.slice(0, 6)}`;
  const totalPages = Math.max(1, Math.ceil(totalPoints / pageSize));
  const pageStart = totalPoints === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(totalPoints, page * pageSize);

  const findPointCell = (p, r, w) =>
    p.cells.find((c) => c.radius_m === r && c.time_window_days === w)?.count ?? 0;

  const exportCSV = async () => {
    // CSV-quote any cell containing commas, quotes, or newlines.
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const radiusName = { 0: "Exact", 250: "250m", 500: "500m", 1000: "1km" };

    // For large batches, fetch the full point set in one go for the CSV.
    let fullPoints = points;
    if (totalPoints > points.length) {
      try {
        toast.loading("Preparing full export…", { id: "csv-export" });
        const { data: full } = await api.get(`/audience/batches/${id}`, {
          params: { skip: 0, limit: totalPoints, map_sample: 0 },
        });
        fullPoints = full.points || points;
        toast.dismiss("csv-export");
      } catch {
        toast.error("Could not load full dataset; exporting current page only", { id: "csv-export" });
      }
    }

    const rows = [];

    // ── Header block ──
    rows.push([`Audience export · ${title}`]);
    rows.push([`Generated`, new Date().toISOString()]);
    rows.push([`Batch ID`, batch.id]);
    rows.push([`Type`, isSingle ? "Single query" : "Bulk upload"]);
    rows.push([`Points`, fullPoints.length]);
    rows.push([]);

    // ── Section 1: Aggregated matrix (radii × windows) ──
    rows.push(["Aggregated matrix · summed across all points"]);
    rows.push(["Radius \\ Time window", ...WINDOWS.map((w) => `${w} days`)]);
    for (const r of RADII) {
      rows.push([radiusName[r], ...WINDOWS.map((w) => matrix[r]?.[w] ?? 0)]);
    }
    rows.push([]);

    // ── Section 2: Per-point pivot ──
    rows.push(["Per-point pivot"]);
    const pivotHeaderTop = ["", "", ""];
    const pivotHeaderBot = ["Label", "Latitude", "Longitude"];
    for (const r of RADII) {
      for (const w of WINDOWS) {
        pivotHeaderTop.push(radiusName[r]);
        pivotHeaderBot.push(`${w}d`);
      }
    }
    rows.push(pivotHeaderTop);
    rows.push(pivotHeaderBot);
    for (const p of fullPoints) {
      const row = [p.label || "", p.latitude, p.longitude];
      for (const r of RADII) {
        for (const w of WINDOWS) {
          row.push(findPointCell(p, r, w));
        }
      }
      rows.push(row);
    }

    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audience_${batch.id}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${fullPoints.length.toLocaleString()} points`);
  };

  const activate = (r, w) => {
    if (!(user.role === "admin" || user.role === "campaign_manager")) {
      toast.error("Only Admins / Campaign Managers can push campaigns.");
      return;
    }
    // Use the first available point as targeting reference; aggregate count comes from matrix.
    const p = points[0] || mapPoints[0];
    if (!p) {
      toast.error("No points available to activate.");
      return;
    }
    navigate("/campaigns/new", {
      state: {
        prefill: {
          latitude: p.latitude,
          longitude: p.longitude,
          radius_m: r,
          time_window_days: w,
          audience_count: matrix[r][w] || 0,
          audience_result_id: p.result_id,
          label: title,
        },
      },
    });
  };

  // colour scale for map markers based on the current radius/window count
  const counts = mapPoints.map((p) => findPointCell(p, mapRadius, mapWindow));
  const maxCount = Math.max(1, ...counts);

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1500px]">
      <Link to="/search" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.15em] text-[#555] hover:text-[#0A0A0A] mb-6">
        <ArrowLeft size={12} /> Back to audiences
      </Link>

      <PageHeader
        eyebrow={`${isSingle ? "SINGLE" : "BULK"} · ${totalPoints} point${totalPoints === 1 ? "" : "s"}`}
        title={title}
        description={`${new Date(batch.created_at).toLocaleString()} · ${batch.processed} processed${batch.failed ? ` · ${batch.failed} failed` : ""}`}
        testid="batch-detail-header"
        actions={
          <button onClick={exportCSV} data-testid="batch-export-csv" className="border border-[#0A0A0A] px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center gap-2">
            <Download size={12} /> Export pivot CSV
          </button>
        }
      />

      {/* Map with filter */}
      <section className="mb-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-3">
          <SectionLabel>Audience map · {totalPoints} coordinate{totalPoints === 1 ? "" : "s"}</SectionLabel>
          <div className="flex items-center gap-3 flex-wrap" data-testid="map-filter">
            <button
              type="button"
              onClick={() => setShowHeatmap((v) => !v)}
              data-testid="heatmap-toggle"
              aria-pressed={showHeatmap}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] border transition-colors ${
                showHeatmap
                  ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                  : "bg-white text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
              }`}
              title={points.length < 2 ? "Heatmap is most useful with 2+ points" : "Toggle density heatmap"}
            >
              <Flame size={12} /> Heatmap {showHeatmap ? "on" : "off"}
            </button>
            <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">
              Radius
              <select
                data-testid="map-radius-select"
                value={mapRadius}
                onChange={(e) => setMapRadius(Number(e.target.value))}
                className="ml-2 bg-white border border-[#E0E0D8] rounded-none px-2 py-1 font-mono text-xs focus:border-[#FF3E00] focus:outline-none"
              >
                {RADII.map((r) => <option key={r} value={r}>{RADIUS_LABEL[r]}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">
              Time window
              <select
                data-testid="map-window-select"
                value={mapWindow}
                onChange={(e) => setMapWindow(Number(e.target.value))}
                className="ml-2 bg-white border border-[#E0E0D8] rounded-none px-2 py-1 font-mono text-xs focus:border-[#FF3E00] focus:outline-none"
              >
                {WINDOWS.map((w) => <option key={w} value={w}>Last {w} days</option>)}
              </select>
            </label>
          </div>
        </div>

        <div className="border border-[#0A0A0A] bg-white relative overflow-hidden" data-testid="audience-map" style={{ height: 460 }}>
          <MapContainer
            key={batch.id}
            style={{ height: "100%", width: "100%" }}
            center={[mapPoints[0]?.latitude || 0, mapPoints[0]?.longitude || 0]}
            zoom={4}
            scrollWheelZoom
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <FitBounds points={mapPoints} />
            {showHeatmap && (
              <HeatLayer
                points={mapPoints}
                radius={mapRadius}
                window={mapWindow}
                maxCount={maxCount}
              />
            )}
            {mapPoints.map((p) => {
              const c = findPointCell(p, mapRadius, mapWindow);
              const intensity = Math.max(0.35, c / maxCount);
              return (
                <CircleMarker
                  key={p.query_id}
                  center={[p.latitude, p.longitude]}
                  radius={9 + intensity * 10}
                  pathOptions={{
                    color: "#FF3E00",
                    weight: 2,
                    fillColor: "#FF3E00",
                    fillOpacity: showHeatmap ? 0.2 : intensity * 0.65,
                    className: "geo-pulse",
                  }}
                >
                  <LeafletTooltip direction="top" offset={[0, -10]} opacity={1}>
                    <div className="font-mono text-xs">
                      <div className="font-semibold text-[#0A0A0A]">{p.label || `${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)}`}</div>
                      <div className="text-[#555] mt-0.5">{RADIUS_LABEL[mapRadius]} · last {mapWindow} days</div>
                      <div className="text-[#FF3E00] text-base font-semibold mt-1">{fmt(c)} devices</div>
                    </div>
                  </LeafletTooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {showHeatmap && (
            <div className="absolute bottom-3 left-3 z-[1000] bg-white border border-[#0A0A0A] px-3 py-2 shadow-[3px_3px_0_0_rgba(10,10,10,1)]" data-testid="heatmap-legend">
              <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#555] mb-1.5">Density</p>
              <div className="flex items-center gap-2">
                <div
                  className="h-2 w-32"
                  style={{ background: "linear-gradient(to right, #0A0A0A, #0052FF, #FF3E00, #FF8A3D, #FFE000)" }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[9px] font-mono text-[#888]">low</span>
                <span className="text-[9px] font-mono text-[#888]">high</span>
              </div>
            </div>
          )}
        </div>
        <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.15em] text-[#888]">
          {totalPoints > mapPoints.length && (
            <span className="text-[#FF3E00] mr-2">Showing {mapPoints.length.toLocaleString()} of {totalPoints.toLocaleString()} points (sampled for performance) ·</span>
          )}
          {showHeatmap
            ? "Heatmap intensity is normalised to the highest-density point at the selected radius and time window."
            : "Hover any marker for the audience count at the selected radius and time window."}
        </p>
      </section>

      {/* Aggregated matrix */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>
            {isSingle ? "Audience matrix" : `Aggregated audience matrix · summed across ${totalPoints} points`}
          </SectionLabel>
        </div>
        <div className="border border-[#E0E0D8] bg-white overflow-x-auto">
          <table className="w-full border-collapse" data-testid="audience-matrix">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A]">
                <th className="text-left py-4 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555] w-32">Radius</th>
                {WINDOWS.map((w) => (
                  <th key={w} className="text-left py-4 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">
                    Last {w} days
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {RADII.map((r) => (
                <tr key={r} className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0] transition-colors">
                  <td className="py-5 px-4 font-mono text-xs uppercase tracking-wider text-[#0A0A0A] border-r border-[#E0E0D8]">
                    {RADIUS_LABEL[r]}
                  </td>
                  {WINDOWS.map((w) => (
                    <td key={w} className="py-5 px-4 border-r border-[#E0E0D8] last:border-r-0 group">
                      <div className="flex items-baseline justify-between gap-3">
                        <span data-testid={`cell-${r}-${w}`} className="font-mono text-2xl tracking-tighter">
                          {fmt(matrix[r]?.[w] || 0)}
                        </span>
                        <button
                          onClick={() => activate(r, w)}
                          data-testid={`activate-${r}-${w}`}
                          className="text-[10px] uppercase font-mono tracking-wider text-[#FF3E00] opacity-60 group-hover:opacity-100 focus:opacity-100 flex items-center gap-1 hover:underline transition-opacity"
                        >
                          <Megaphone size={11} /> Activate <ArrowRight size={11} />
                        </button>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-point details (only for bulk) */}
      {!isSingle && (
        <section data-testid="per-point-section">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-3">
            <SectionLabel>Per-point breakdown</SectionLabel>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">
                Page size
                <select
                  data-testid="page-size-select"
                  value={pageSize}
                  onChange={(e) => { setPaging(true); setPage(1); setPageSize(Number(e.target.value)); }}
                  className="ml-2 bg-white border border-[#E0E0D8] rounded-none px-2 py-1 font-mono text-xs focus:border-[#FF3E00] focus:outline-none"
                >
                  {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </label>
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]" data-testid="page-range">
                {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} of {totalPoints.toLocaleString()}
              </p>
            </div>
          </div>
          <div className={`border border-[#E0E0D8] bg-white overflow-x-auto transition-opacity ${paging ? "opacity-50" : ""}`}>
            <table className="w-full border-collapse" data-testid="per-point-table">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A]">
                  <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">#</th>
                  <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Label</th>
                  <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Lat / Lon</th>
                  {WINDOWS.map((w) => (
                    <th key={w} className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">{w}d · 1km</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {points.map((p, idx) => (
                  <tr key={p.query_id} className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0]">
                    <td className="py-3 px-4 font-mono text-xs text-[#888]">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="py-3 px-4 text-sm">{p.label || "—"}</td>
                    <td className="py-3 px-4 font-mono text-xs">{p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}</td>
                    {WINDOWS.map((w) => (
                      <td key={w} className="py-3 px-4 text-right font-mono">{fmt(findPointCell(p, 1000, w))}</td>
                    ))}
                  </tr>
                ))}
                {points.length === 0 && (
                  <tr><td colSpan={3 + WINDOWS.length} className="py-8 text-center text-sm text-[#888] font-mono">No points on this page.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className="flex items-center justify-between mt-4" data-testid="pagination-controls">
            <button
              type="button"
              data-testid="page-prev"
              disabled={page <= 1 || paging}
              onClick={() => { setPaging(true); setPage((p) => Math.max(1, p - 1)); }}
              className="flex items-center gap-1.5 border border-[#0A0A0A] px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={12} /> Prev
            </button>
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">
              <span>Page</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={page}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(totalPages, Number(e.target.value) || 1));
                  setPaging(true); setPage(v);
                }}
                data-testid="page-input"
                className="w-16 text-center bg-white border border-[#E0E0D8] px-2 py-1 font-mono text-xs focus:border-[#FF3E00] focus:outline-none"
              />
              <span>of {totalPages.toLocaleString()}</span>
            </div>
            <button
              type="button"
              data-testid="page-next"
              disabled={page >= totalPages || paging}
              onClick={() => { setPaging(true); setPage((p) => Math.min(totalPages, p + 1)); }}
              className="flex items-center gap-1.5 border border-[#0A0A0A] px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight size={12} />
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
